<template>
  <v-app>
    <div
      v-if="isOffline"
      class="animate__animated animate__slideInDown"
      style="
        position: fixed;
        z-index: 999;
        font-size: smaller;
        height: 25px;
        font-weight: bold;
        width: 100%;
        background: #d91e1e;
        padding: 3px;
        text-align: center;
        color: white;
      "
    >
      OFFLINE MODE
    </div>
    <component
      :is="currentLayout"
      v-if="isRouterLoaded"
    >
      <transition
        name="fade"
        mode="out-in"
      >
        <router-view />
      </transition>
    </component>
    <v-snackbar
      color="#F2F5F8"
      elevation="24"
      style="z-index: 999; position: fixed"
      :timeout="info.timeout"
      id="arch-error"
      :absolute="true"
      v-model="info.visible"
      :vertical="true"
      top
    >
      <div
        v-if="info.icon != ''"
        style="text-align: center; margin-bottom: 5px"
      >
        <v-icon color="rgb(156 15 27)">
          {{ info.icon }}
        </v-icon>
      </div>
      <div
        style="text-align: center"
        :style="{ color: info.textColor }"
        v-html="info.body"
      />
      <template #action="{ attrs }">
        <v-btn
          color="indigo"
          text
          v-bind="attrs"
          @click="showHideMessage(false)"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <v-dialog
      v-model="archConfirm.show"
      persistent
      max-width="400"
    >
      <v-card>
        <div style="text-align: center; padding: 10px">
          <v-icon
            size="30px"
            :color="archConfirmIcon.color"
          >
            {{
              archConfirmIcon.icon
            }}
          </v-icon>
        </div>
        <div
          class="headline"
          style="text-align: center"
        >
          {{ archConfirm.title }}
        </div>
        <v-card-text
          class="text-secondary text--darken-4"
          style="text-align: center"
        >
          {{ archConfirm.message }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            :x-large="archConfirm.isTouch"
            @click="archConfirmCallback(false)"
          >
            {{ archConfirm.no }}
          </v-btn>
          <v-btn
            :x-large="archConfirm.isTouch"
            :loading="archConfirm.closing"
            color="primary"
            @click="archConfirmCallback(true)"
          >
            {{ archConfirm.yes }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <NewVersionNotification
      v-model="updateNotificationVisible"
      @update-available="this.onUpdateAvailable"
    />
  </v-app>
</template>
<script>
import { mapState, mapMutations } from "vuex";
import app from "@/store/app/index";
import config from "./configs";
import defaultLayout from "./layouts/DefaultLayout";
import simpleLayout from "./layouts/SimpleLayout";
import authLayout from "./layouts/AuthLayout";
import errorLayout from "./layouts/ErrorLayout";
import { Api } from "@/services/Api";
import worker from "@/workers/Index";
import NewVersionNotification from "sentec-vues/modules/service-worker/view/NewVersionNotification";
import { ConfigurationService, ConfigurationCacheInvalidator } from "sentec-core-ts";
import signalGo from "@/services/SignalGo";
import Vue from "vue";
import { OrderNotificationService } from "@/services/OrderNotificationService";
async function connectionCheck() {
  app.state.isOffline = !(await Api.isOnline());
  setTimeout(async () => {
    await connectionCheck();
  }, 20000);
}
export default {
  components: {
    NewVersionNotification,
    defaultLayout,
    simpleLayout,
    authLayout,
    errorLayout,
  },
  data() {
    return {
      updateNotificationVisible: false,
      sw_registration: null,
      orderNotifService: null,
    };
  },
  created() {
    this.$setTitle(config.product.name);
    this.injectAwsWafScript();
  },
  computed: {
    ...mapState("app", [
      "toast",
      "hotels",
      "archConfirm",
      "isOffline",
      "isSyncing",
    ]),
    isRouterLoaded: function () {
      return this.$route.name !== null;
    },
    currentLayout: function () {
      const layout = this.$route.meta.layout || "default";

      return layout + "Layout";
    },
    archConfirmIcon() {
      const temp = this.archConfirm.icons["info"];

      return this.archConfirm.icons[this.archConfirm.type] || temp;
    },
    info() {
      return this.$store.state.app.info;
    },
  },
  head: {
    link: [...config.icons.map((href) => ({ rel: "stylesheet", href }))],
  },
  methods: {
    ...mapMutations("app", ["archConfirmCallback", "showHideMessage"]),
    onUpdateAvailable() {
      this.updateNotificationVisible = true;
    },
    // Inject once per page load; the WAF SDK refreshes its own token thereafter.
    injectAwsWafScript() {
      const id = "aws-waf";
      const src = process.env.VUE_APP_AWS_WAF_PATH;
      if (!src || document.getElementById(id) !== null) {
        return;
      }
      const s = document.createElement("script");
      s.setAttribute("src", src);
      s.setAttribute("id", id);
      document.head.appendChild(s);
    },
    initConfigService() {
      const hotelId = Number(this.$route?.query?.hotelId) || 0;
      if (!hotelId) return;

      // Skip if already initialized for this hotel
      if (Vue.prototype.$config && Vue.prototype.$config.hotelId === hotelId) return;

      try {
        Vue.prototype.$config = new ConfigurationService(this.$http, hotelId);
        ConfigurationCacheInvalidator.subscribe(hotelId, signalGo);
      } catch (err) {
        console.error("[App] failed to initialize ConfigurationService:", err);
      }
    },
    initOrderNotifications() {
      if (!this.orderNotifService) {
        this.orderNotifService = new OrderNotificationService(this);
      }
      this.orderNotifService.init().catch((err) => {
        console.error("[App] failed to initialize order notifications:", err);
      });
    },
  },
  async mounted() {
    app.state.window.width = window.innerWidth;
    app.state.window.height = window.innerHeight;
    this.initConfigService();
    this.initOrderNotifications();
    await connectionCheck();
    window.addEventListener("offline", function (e) {
      app.state.isOffline = true;
      console.error("offline");
    });
    window.addEventListener("online", function (e) {
      app.state.isOffline = false;
      console.info("online");
    });
    window.addEventListener("resize", (e) => {
      app.state.window.width = e.target.outerWidth;
      app.state.window.height = e.target.outerHeight;
    });
  },
  watch: {
    isOffline(newValue, oldValue) {
      if (!newValue && oldValue) {
        worker.Run();
      }
    },
    isSyncing(newValue) {
      if (newValue) {
        this.$archNotify({
          message: "Syncing pending requests!",
          color: "success",
        });
      }
    },
    $route() {
      this.initConfigService();
      this.initOrderNotifications();
    },
  },
};
</script>

<style>
#arch-error div div:nth-child(1) {
  width: 100% !important;
}
#nprogress .bar {
  background: #ec1721 !important;
}
#nprogress .spinner-icon {
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: solid 2px transparent;
  border-top-color: #ec1721 !important;
  border-left-color: #ec1721 !important;
}
#nprogress .spinner {
  top: 2px;
  right: 2px;
}
</style>
